// QR Scanner component using html5-qrcode
import { Html5Qrcode } from 'html5-qrcode';

let scanner = null;
let isScanning = false;
let availableCameras = [];
let currentCameraIndex = 0;

// Create scanner modal HTML
export function createScannerModal() {
  const modal = document.createElement('div');
  modal.id = 'qr-scanner-modal';
  modal.className = 'qr-scanner-modal hidden';
  modal.innerHTML = `
    <div class="qr-scanner-overlay"></div>
    <div class="qr-scanner-content">
      <button class="qr-scanner-close" aria-label="Close scanner">
        <i class="fas fa-times"></i>
      </button>
      <button class="qr-camera-toggle" aria-label="Switch camera" style="display: none;">
        <i class="fas fa-camera-rotate"></i>
      </button>
      <h2 class="qr-scanner-title">Scan QR Code</h2>
      <p class="qr-scanner-subtitle">Point your camera at the restaurant's QR code</p>
      <div id="qr-reader" class="qr-reader"></div>
      <p class="qr-scanner-help">Make sure the QR code is well-lit and in focus</p>
    </div>
  `;
  document.body.appendChild(modal);
  
  // Setup close button
  const closeBtn = modal.querySelector('.qr-scanner-close');
  closeBtn.addEventListener('click', closeScannerModal);
  
  // Setup camera toggle button
  const toggleBtn = modal.querySelector('.qr-camera-toggle');
  toggleBtn.addEventListener('click', switchCamera);
  
  // Close on overlay click
  const overlay = modal.querySelector('.qr-scanner-overlay');
  overlay.addEventListener('click', closeScannerModal);
  
  return modal;
}

// Open scanner modal
export async function openScannerModal() {
  let modal = document.getElementById('qr-scanner-modal');
  
  if (!modal) {
    modal = createScannerModal();
  }
  
  modal.classList.remove('hidden');
  document.body.classList.add('scanner-open');
  
  // Start scanning
  await startScanning();
}

// Close scanner modal
export function closeScannerModal() {
  const modal = document.getElementById('qr-scanner-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
  document.body.classList.remove('scanner-open');
  
  stopScanning();
}

// Start QR code scanning
async function startScanning() {
  if (isScanning) return;
  
  try {
    scanner = new Html5Qrcode("qr-reader");
    
    // Get available cameras
    availableCameras = await Html5Qrcode.getCameras();
    console.log('📷 Available cameras:', availableCameras);
    
    // Find rear camera (prefer "back" or "environment" in label)
    currentCameraIndex = availableCameras.findIndex(camera => 
      camera.label && (
        camera.label.toLowerCase().includes('back') ||
        camera.label.toLowerCase().includes('rear') ||
        camera.label.toLowerCase().includes('environment')
      )
    );
    
    // If no rear camera found, use last camera (usually rear on mobile)
    if (currentCameraIndex === -1 && availableCameras.length > 0) {
      currentCameraIndex = availableCameras.length - 1;
    }
    
    // Show camera toggle button if multiple cameras available
    const toggleBtn = document.querySelector('.qr-camera-toggle');
    if (toggleBtn && availableCameras.length > 1) {
      toggleBtn.style.display = 'flex';
    }
    
    // Start with selected camera
    const cameraId = availableCameras[currentCameraIndex]?.id;
    
    await scanner.start(
      cameraId || { facingMode: "environment" },
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      },
      onScanSuccess,
      onScanError
    );
    
    isScanning = true;
    console.log('📷 QR Scanner started with camera:', availableCameras[currentCameraIndex]?.label);
  } catch (err) {
    console.error('Failed to start scanner:', err);
    isScanning = false;
    alert('Unable to access camera. Please check permissions.');
    closeScannerModal();
  }
}

// Stop scanning
async function stopScanning() {
  if (scanner && isScanning) {
    try {
      await scanner.stop();
      scanner.clear();
      scanner = null;
      isScanning = false;
      console.log('📷 QR Scanner stopped');
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
  }
}

// Handle successful scan
function onScanSuccess(decodedText, decodedResult) {
  console.log('✅ QR Code scanned:', decodedText);
  
  // Extract secret from URL
  try {
    const url = new URL(decodedText);
    const secret = url.searchParams.get('secret');
    
    if (secret) {
      console.log('🔑 Secret found:', secret);
      
      // Stop scanner and close modal
      stopScanning();
      closeScannerModal();
      
      // Navigate to check-in page
      const basePath = window.location.hostname.includes('fogg.app') ? '/' : '/fogg-app/';
      window.location.href = `${basePath}checkin.html?secret=${secret}`;
    } else {
      console.warn('No secret found in QR code');
      alert('Invalid QR code: No check-in code found');
    }
  } catch (err) {
    console.error('Invalid QR code URL:', err);
    alert('Invalid QR code format');
  }
}

// Handle scan errors (can be ignored for continuous scanning)
function onScanError(errorMessage) {
  // Suppress continuous scanning errors
  // console.log('Scan error:', errorMessage);
}

// Switch camera function
async function switchCamera() {
  if (!scanner || !isScanning || availableCameras.length < 2) return;
  
  try {
    // Stop current scanner
    await scanner.stop();
    
    // Switch to next camera
    currentCameraIndex = (currentCameraIndex + 1) % availableCameras.length;
    const cameraId = availableCameras[currentCameraIndex].id;
    
    // Restart with new camera
    await scanner.start(
      cameraId,
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      },
      onScanSuccess,
      onScanError
    );
    
    console.log('📷 Switched to camera:', availableCameras[currentCameraIndex]?.label);
  } catch (err) {
    console.error('Error switching camera:', err);
    // If switching fails, try to restart with environment facing mode
    try {
      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        onScanSuccess,
        onScanError
      );
    } catch (restartErr) {
      console.error('Failed to restart camera:', restartErr);
    }
  }
}

// Initialize scanner styles
export function initScannerStyles() {
  if (document.getElementById('qr-scanner-styles')) return;
  
  const styles = document.createElement('style');
  styles.id = 'qr-scanner-styles';
  styles.textContent = `
    .qr-scanner-modal {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .qr-scanner-modal.hidden {
      display: none;
    }
    
    .qr-scanner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
    }
    
    .qr-scanner-content {
      position: relative;
      max-width: 500px;
      width: 90%;
      background: #1a1a1a;
      border-radius: 16px;
      padding: 2rem;
      z-index: 10001;
    }
    
    .qr-scanner-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #fff;
      font-size: 1.25rem;
      transition: background 0.3s;
    }
    
    .qr-scanner-close:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    
    .qr-camera-toggle {
      position: absolute;
      top: 1rem;
      left: 1rem;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #fff;
      font-size: 1.25rem;
      transition: background 0.3s;
    }
    
    .qr-camera-toggle:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    
    .qr-scanner-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 0.5rem;
      color: #fff;
      text-align: center;
    }
    
    .qr-scanner-subtitle {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.7);
      text-align: center;
      margin: 0 0 1.5rem;
    }
    
    .qr-reader {
      border-radius: 12px;
      overflow: hidden;
      margin: 0 auto;
    }
    
    .qr-scanner-help {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.6);
      text-align: center;
      margin: 1rem 0 0;
    }
    
    body.scanner-open {
      overflow: hidden;
    }
    
    /* Override html5-qrcode styles */
    #qr-reader {
      border: 2px solid #00ffcc !important;
    }
    
    #qr-reader__dashboard_section_csr button {
      background: #00ffcc !important;
      color: #000 !important;
      border: none !important;
      border-radius: 8px !important;
      padding: 8px 16px !important;
      font-weight: 600 !important;
    }
  `;
  document.head.appendChild(styles);
}
